import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Plus, Trash2, Save, RotateCcw, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PricingAdmin() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyBulk, setBusyBulk] = useState(false);

  useEffect(() => {
    http.get("/public/pricing").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="font-ui text-[10px] tracking-[0.32em] text-[#737373] py-10"><Loader2 className="inline animate-spin mr-2" size={14}/> Loading pricing…</div>;

  const setField = (k, v) => setData(d => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: d2 } = await http.put("/admin/pricing", {
        default_variants: data.default_variants,
        volume_discounts: data.volume_discounts,
        cart_tier_discounts: data.cart_tier_discounts,
        default_shipping: data.default_shipping,
        site_special: data.site_special,
      });
      setData(d2);
      toast.success("Pricing saved.");
    } catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const applyBulk = async () => {
    if (!window.confirm("Re-apply the defaults to EVERY artwork? This overwrites any per-artwork variant prices.")) return;
    setBusyBulk(true);
    try {
      const { data: r } = await http.post("/admin/pricing/apply-defaults-to-all");
      toast.success(`Defaults applied to ${r.updated} artworks.`);
    } catch (e) { toast.error("Bulk update failed"); }
    finally { setBusyBulk(false); }
  };

  return (
    <div className="space-y-8" data-testid="pricing-admin">
      {/* Header / save bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 sticky top-0 bg-black/90 backdrop-blur py-3 z-10 border-b border-white/5">
        <div>
          <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055]">Central Pricing</div>
          <h2 className="font-display-saira text-2xl text-[#F5F2EB]">Defaults · Specials · Volume</h2>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="pricing-apply-bulk" onClick={applyBulk} disabled={busyBulk} className="btn-ghost"><RotateCcw size={14}/> {busyBulk ? "Applying…" : "Apply defaults to all artworks"}</button>
          <button data-testid="pricing-save" onClick={save} disabled={saving} className="btn-gold"><Save size={14}/> {saving ? "Saving…" : "Save changes"}</button>
        </div>
      </div>

      {/* Default Variants */}
      <section className="border border-white/10 bg-[#0a0a0a] p-5">
        <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-4">Default Variant Prices</div>
        <p className="font-body text-[12px] text-[#737373] mb-4">Each artwork inherits these prices unless overridden in the artwork editor.</p>
        <div className="space-y-2">
          {data.default_variants.map((v, i) => (
            <div key={v.key + i} className="grid grid-cols-12 gap-2 items-center" data-testid={`variant-row-${v.key}`}>
              <input className="stc-input col-span-3 font-mono text-sm" value={v.key} onChange={e => updateAt(setData, "default_variants", i, "key", e.target.value)} placeholder="key"/>
              <input className="stc-input col-span-4" value={v.label} onChange={e => updateAt(setData, "default_variants", i, "label", e.target.value)} placeholder="Label"/>
              <input type="number" step="0.01" className="stc-input col-span-2 text-right font-mono" value={v.price} onChange={e => updateAt(setData, "default_variants", i, "price", parseFloat(e.target.value) || 0)}/>
              <input type="number" className="stc-input col-span-1 text-right font-mono" value={v.weight || 0} onChange={e => updateAt(setData, "default_variants", i, "weight", parseFloat(e.target.value) || 0)} title="Weight (oz)"/>
              <select className="stc-input col-span-1 text-xs" value={v.type || "regular"} onChange={e => updateAt(setData, "default_variants", i, "type", e.target.value)}>
                <option value="regular">regular</option><option value="digital">digital</option><option value="upgrade">upgrade</option>
              </select>
              <label className="col-span-1 inline-flex items-center gap-1 text-[10px] text-[#9a9690]">
                <input type="checkbox" checked={v.active !== false} onChange={e => updateAt(setData, "default_variants", i, "active", e.target.checked)}/>on
              </label>
              <button type="button" onClick={() => removeAt(setData, "default_variants", i)} className="col-span-12 sm:col-span-0 text-right text-[#9a9690] hover:text-red-400"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
        <button onClick={() => appendItem(setData, "default_variants", { key: "new_variant", label: "New Variant", price: 0, weight: 0, type: "regular", active: true })} className="btn-ghost mt-3" data-testid="pricing-add-variant"><Plus size={14}/> Add variant</button>
      </section>

      {/* Shipping */}
      <section className="border border-white/10 bg-[#0a0a0a] p-5">
        <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-4">Shipping</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="block"><span className="text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Flat rate ($)</span>
            <input data-testid="ship-flat" type="number" step="0.01" className="stc-input mt-1 font-mono" value={data.default_shipping.flat || 0} onChange={e => setField("default_shipping", { ...data.default_shipping, flat: parseFloat(e.target.value) || 0 })}/>
          </label>
          <label className="block"><span className="text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Free over ($)</span>
            <input data-testid="ship-free-over" type="number" step="0.01" className="stc-input mt-1 font-mono" value={data.default_shipping.free_over || 0} onChange={e => setField("default_shipping", { ...data.default_shipping, free_over: parseFloat(e.target.value) || 0 })}/>
          </label>
          <label className="block flex items-end gap-2 pb-2">
            <input type="checkbox" checked={!!data.default_shipping.digital_only_free} onChange={e => setField("default_shipping", { ...data.default_shipping, digital_only_free: e.target.checked })}/>
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Digital-only orders ship free</span>
          </label>
        </div>
      </section>

      {/* Volume per-variant */}
      <section className="border border-white/10 bg-[#0a0a0a] p-5">
        <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-4">Volume Discount (per variant)</div>
        <p className="font-body text-[12px] text-[#737373] mb-4">Example: 11x14 — buy 2 for $30 total. Add as many tiers as you like.</p>
        {data.default_variants.filter(v => v.type !== "upgrade").map(v => {
          const rules = data.volume_discounts?.[v.key] || [];
          return (
            <div key={v.key} className="border-t border-white/5 py-3" data-testid={`volume-${v.key}`}>
              <div className="font-ui-tight text-[#F5F2EB] mb-2">{v.label} <span className="text-[10px] text-[#737373] font-mono">({v.key})</span></div>
              <div className="space-y-2">
                {rules.map((r, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-[10px] text-[#9a9690]">Buy</span>
                    <input type="number" min="2" className="stc-input w-20 font-mono text-center" value={r.min_qty} onChange={e => updateNested(setData, "volume_discounts", v.key, i, "min_qty", parseInt(e.target.value) || 0)}/>
                    <span className="text-[10px] text-[#9a9690]">for total $</span>
                    <input type="number" step="0.01" className="stc-input w-28 font-mono text-center" value={r.total_price} onChange={e => updateNested(setData, "volume_discounts", v.key, i, "total_price", parseFloat(e.target.value) || 0)}/>
                    <button onClick={() => removeNested(setData, "volume_discounts", v.key, i)} className="text-[#9a9690] hover:text-red-400"><Trash2 size={14}/></button>
                  </div>
                ))}
              </div>
              <button onClick={() => appendNested(setData, "volume_discounts", v.key, { min_qty: 2, total_price: v.price * 1.5 })} className="btn-ghost mt-2 text-[11px]"><Plus size={12}/> Add tier</button>
            </div>
          );
        })}
      </section>

      {/* Cart-wide tiers */}
      <section className="border border-white/10 bg-[#0a0a0a] p-5">
        <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-4">Cart-wide Bundle Discount</div>
        <p className="font-body text-[12px] text-[#737373] mb-4">% off the entire cart subtotal when total item count reaches each threshold. Best matching tier wins.</p>
        <div className="space-y-2">
          {(data.cart_tier_discounts || []).map((t, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`cart-tier-${i}`}>
              <span className="text-[10px] text-[#9a9690]">Any</span>
              <input type="number" min="2" className="stc-input w-20 font-mono text-center" value={t.min_qty} onChange={e => updateAt(setData, "cart_tier_discounts", i, "min_qty", parseInt(e.target.value) || 0)}/>
              <span className="text-[10px] text-[#9a9690]">+ items →</span>
              <input type="number" min="0" max="80" className="stc-input w-20 font-mono text-center" value={t.pct_off} onChange={e => updateAt(setData, "cart_tier_discounts", i, "pct_off", parseFloat(e.target.value) || 0)}/>
              <span className="text-[10px] text-[#9a9690]">% off</span>
              <button onClick={() => removeAt(setData, "cart_tier_discounts", i)} className="text-[#9a9690] hover:text-red-400"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
        <button onClick={() => appendItem(setData, "cart_tier_discounts", { min_qty: 2, pct_off: 10 })} className="btn-ghost mt-2" data-testid="add-cart-tier"><Plus size={14}/> Add tier</button>
      </section>

      {/* Site special */}
      <section className="border border-[#C9A055]/30 bg-gradient-to-br from-[#1a1814] to-[#0a0a0a] p-5" data-testid="site-special">
        <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-4 flex items-center gap-2"><Sparkles size={14}/> Site-wide Special</div>
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <label className="block flex items-center gap-2">
            <input data-testid="site-special-active" type="checkbox" checked={!!data.site_special.active} onChange={e => setField("site_special", { ...data.site_special, active: e.target.checked })}/>
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#F5F2EB]">Active</span>
          </label>
          <label className="block"><span className="text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Label</span>
            <input className="stc-input mt-1" value={data.site_special.label || ""} onChange={e => setField("site_special", { ...data.site_special, label: e.target.value })} placeholder="FLASH SALE"/>
          </label>
          <label className="block"><span className="text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">% off everything</span>
            <input type="number" min="0" max="80" className="stc-input mt-1 font-mono" value={data.site_special.pct_off || 0} onChange={e => setField("site_special", { ...data.site_special, pct_off: parseFloat(e.target.value) || 0 })}/>
          </label>
          <div className="font-display-saira text-2xl text-[#C9A055]">{data.site_special.active ? `${data.site_special.pct_off || 0}% OFF` : "Off"}</div>
        </div>
      </section>
    </div>
  );
}

// Tiny helpers (avoid plumbing setState through many components)
function updateAt(setter, key, idx, field, value) {
  setter(d => ({ ...d, [key]: d[key].map((row, i) => i === idx ? { ...row, [field]: value } : row) }));
}
function appendItem(setter, key, item) { setter(d => ({ ...d, [key]: [...(d[key] || []), item] })); }
function removeAt(setter, key, idx) { setter(d => ({ ...d, [key]: d[key].filter((_, i) => i !== idx) })); }
function updateNested(setter, key, sub, idx, field, value) {
  setter(d => ({ ...d, [key]: { ...(d[key] || {}), [sub]: (d[key]?.[sub] || []).map((r, i) => i === idx ? { ...r, [field]: value } : r) } }));
}
function appendNested(setter, key, sub, item) {
  setter(d => ({ ...d, [key]: { ...(d[key] || {}), [sub]: [...((d[key] || {})[sub] || []), item] } }));
}
function removeNested(setter, key, sub, idx) {
  setter(d => ({ ...d, [key]: { ...(d[key] || {}), [sub]: ((d[key] || {})[sub] || []).filter((_, i) => i !== idx) } }));
}
